import get from 'lodash.get';

import {
  LAYER_SEATS_ID,
  LAYER_SECTIONS_ID,
  LAYER_ROWS_ID,
  LAYER_SEATS_HIGHLIGHTED_ID,
  LAYER_BACKGROUND_SOURCE,
  LAYER_SECTIONS_SOURCE,
  TILESET_URL,
  IMAGE_SOURCE_URL,
  UNDEFINED_TYPE,
} from '../mapbox-constants/mapbox_constants';

const urlWithMode = (url, mode = '') => {
  if (typeof url === 'string' && typeof mode === 'string') {
    if (mode.length > 0) {
      return `${url}-${mode}`;
    }
  }

  return url;
};

const getSectionsTilesetURL = (venueConfigId) => {
  return venueConfigId ? `${TILESET_URL}.${venueConfigId}-${LAYER_SECTIONS_ID}` : null;
};

function sourceExists(mapInstance, sourceId) {
  const mapSource = mapInstance.getSource(sourceId);

  return typeof mapSource !== UNDEFINED_TYPE;
}

function layerExists(mapInstance, layerId) {
  const mapLayer = mapInstance.getLayer(layerId);

  return typeof mapLayer !== UNDEFINED_TYPE;
}

function safeRemoveLayer(mapInstance, layerConfigId) {
  const hasLayer = layerExists(mapInstance, layerConfigId);
  if (hasLayer) {
    mapInstance.removeLayer(layerConfigId);
  }
}

function safeAddLayer(mapInstance, addedLayerConfig, beforeLayerId = []) {
  if (addedLayerConfig && addedLayerConfig.id) {
    const hasLayer = layerExists(mapInstance, addedLayerConfig.id);
    const hasSource = sourceExists(mapInstance, addedLayerConfig.source);
    let hasAdded = false;
    if (hasLayer) {
      mapInstance.removeLayer(addedLayerConfig.id);
    }
    if (hasSource) {
      if (Array.isArray(beforeLayerId) && beforeLayerId.length > 0) {
        for (let i = beforeLayerId.length - 1; i >= 0 && !hasAdded; i--) {
          if (layerExists(mapInstance, beforeLayerId[i])) {
            mapInstance.addLayer(addedLayerConfig, beforeLayerId[i]);
            hasAdded = true;
          }
        }
        if (!hasAdded) {
          mapInstance.addLayer(addedLayerConfig);
        }
      } else {
        mapInstance.addLayer(addedLayerConfig);
      }
    }
  }
}

function safeAddSource(mapInstance, sourceConfig) {
  const {id, ...config} = sourceConfig;
  const hasSource = sourceExists(mapInstance, id);
  if (hasSource === false) {
    mapInstance.addSource(id, config);
  }
}

function updateLayer(mapInstance, addecLayerConfig, beforeLayerId = []) {
  safeAddLayer(mapInstance, addecLayerConfig, beforeLayerId);
}

function getSourceLayer(LAYER_ID, venueConfigId) {
  if (LAYER_ID !== undefined && LAYER_ID !== null && venueConfigId !== null && venueConfigId !== undefined) {
    return `${venueConfigId}-${LAYER_ID}`;
  }

  return null;
}

const getSectionsSourceLayer = (venueConfigId) => {
  return getSourceLayer(LAYER_SECTIONS_ID, venueConfigId);
};

const getRowsSourceLayer = (venueConfigId) => {
  return getSourceLayer(LAYER_ROWS_ID, venueConfigId);
};

const getSeatsSourceLayer = (venueConfigId) => {
  return getSourceLayer(LAYER_SEATS_ID, venueConfigId);
};

const getFeatureCoordinates = (feature) => {
  if (feature && feature.geometry && feature.geometry.coordinates) {
    return feature.geometry.coordinates;
  }

  return null;
};

const getUniqueSeatIdsFromListing = (listing) => {
  const result = [];
  if (listing && listing.seats) {
    const {seats} = listing;
    const {sectionId} = listing;

    for (let seatIndex = 0; seatIndex < seats.length; seatIndex++) {
      const {row, seatNumber} = seats[seatIndex];
      result.push(`${sectionId}-${row}-${seatNumber}`);
    }
  }

  return result;
};

const mapIdsToValueStyle = (ids, defaultValue, valueMap) => {
  if (Array.isArray(ids) && ids.length > 0 && defaultValue) {
    const dups = ids.filter((item, index) => ids.indexOf(item) !== index);
    const idsNoDuplicate = [...ids];
    dups.forEach((item) => {
      const index = ids.indexOf(item);
      idsNoDuplicate.splice(index, 1);
    });

    const idColorMap = [];
    for (let idIndex = 0; idIndex < idsNoDuplicate.length; idIndex++) {
      idColorMap.push(`${idsNoDuplicate[idIndex]}`);
      idColorMap.push((valueMap && valueMap[idsNoDuplicate[idIndex]]) || defaultValue);
    }

    return idColorMap;
  }

  return null;
};

const getListingIdFromListing = (listing) => {
  if (listing && listing.seats && Array.isArray(listing.seats) && listing.seats[0].listingId) {
    return listing.seats[0].listingId;
  }

  return null;
};

const getSectionRowSeatFromListing = (listing) => {
  let result = null;

  if (listing && listing.sectionId && listing.seats && Array.isArray(listing.seats)) {
    const {sectionId, seats} = listing;
    const [{row, seatNumber}] = seats;
    result = [sectionId, row, seatNumber];
  }

  return result;
};

const getUniqueRowIdsFromListing = (listingSelected) => {
  const sectionRowSeatId = getSectionRowSeatFromListing(listingSelected);
  if (sectionRowSeatId) {
    const [sectionId, row] = sectionRowSeatId;

    return [`${sectionId}-${row}`];
  }

  return null;
};

const seatProps = (props) => {
  if (props.listingSelected) {
    const {price, sectionName, seats, splitVector, sectionId} = props.listingSelected;
    const [{row, listingId = 0}] = seats;
    let seatsStr = '';
    if (seats.length > 1 && seats[0].seatNumber && seats[seats.length - 1].seatNumber) {
      seatsStr = `${seats[0].seatNumber} - ${seats[seats.length - 1].seatNumber}`;
    } else if (seats[0].seatNumber) {
      seatsStr = `${seats[0].seatNumber}`;
    }

    return {
      sectionId,
      price,
      sectionName,
      seats,
      splitVector,
      seatsStr,
      listingId,
      row,
    };
  }

  return null;
};

const rowProps = (listings = {}) => {
  const startingPrice = getLowestPriceOfARow(listings);
  const rowNumber = get(listings[Object.keys(listings)[0]], 'seats[0].row', null);

  return {
    row: rowNumber,
    startingPrice,
  };
};

/**
 *
 * @param sectionId
 * @param rowId
 * @param seatId
 * @returns {listingsDS|{listing}| undefined}
 */
const getListingFromSectionRowSeatIds = (sectionId, rowId, seatId, props) => {
  return (
    props.listingsDS &&
    props.listingsDS[sectionId] &&
    props.listingsDS[sectionId][rowId] &&
    props.listingsDS[sectionId][rowId][seatId]
  );
};

const getListingsFromSectionRowIds = (sectionId, rowId, props) => {
  return (
    props.listingsRowDSResult && props.listingsRowDSResult[sectionId] && props.listingsRowDSResult[sectionId][rowId]
  );
};

const getLowestPriceOfARow = (listings) => {
  if (listings && Object.keys(listings).length > 0) {
    let minRowPrice = Number.MAX_VALUE;
    let minRowPriceInfo = {};
    for (const listingId of Object.keys(listings)) {
      const price = get(listings[listingId], 'price');
      const amount = get(listings[listingId], 'price.amount');
      if (typeof amount === 'number' && amount < minRowPrice) {
        minRowPrice = amount;
        minRowPriceInfo = price;
      }
    }

    return minRowPrice === Number.MAX_VALUE ? null : minRowPriceInfo;
  }

  return null;
};

const splitVectorMinimumQuantity = (splitVector) => {
  return splitVector && Array.isArray(splitVector) && splitVector.length > 0 ? splitVector[0] : null;
};

const splitVectorValidateQuantity = (splitVector, quantity) => {
  let valid = false;
  if (splitVector && Array.isArray(splitVector) && quantity) {
    valid = splitVector.indexOf(Number(quantity)) > -1;
  }

  return valid;
};

const getValidOrMinimumQuantityFromSplitVector = (splitVector, quantityDesired) => {
  let result = null;
  if (splitVectorValidateQuantity(splitVector, quantityDesired)) {
    result = quantityDesired;
  } else {
    result = splitVectorMinimumQuantity(splitVector);
  }

  return result;
};

const venueConfigId = (props) => {
  return props.venueConfig && props.venueConfig.id;
};

const flattenMultipolygonCoords = (coords = [], result = []) => {
  if (Array.isArray(coords) && Array.isArray(result)) {
    let output = result;
    for (let i = 0; i < coords.length; i++) {
      if (Array.isArray(coords[i]) && typeof coords[i][0] === 'number') {
        output.push(coords[i]);
      } else {
        output = [...flattenMultipolygonCoords(coords[i], output)];
      }
    }

    return output;
  }

  return [];
};

const getRowFeatureByUniqueRowId = (mapInstance, uniqueRowId) => {
  const layerExisting = layerExists(mapInstance, LAYER_ROWS_ID);
  if (layerExisting) {
    const [firstFeature] = mapInstance.queryRenderedFeatures({
      layers: [LAYER_ROWS_ID],
      filter: ['==', 'urid', uniqueRowId],
    });

    return firstFeature;
  }

  return null;
};

const getSeatFeatureByUniqueSeatId = (mapInstance, uniqueSeatId) => {
  const layerExisting = layerExists(mapInstance, LAYER_SEATS_HIGHLIGHTED_ID);
  if (layerExisting) {
    const [firstFeature] = mapInstance.queryRenderedFeatures({
      layers: [LAYER_SEATS_HIGHLIGHTED_ID],
      filter: ['==', 'usid', uniqueSeatId],
    });

    return firstFeature;
  }

  return null;
};

const getsCoordsOfAGroupOfSeats = (seatCoords, uniqueSeatIdArray) => {
  const coordsArray = [];
  if (Array.isArray(uniqueSeatIdArray) && seatCoords) {
    for (let i = 0; i < uniqueSeatIdArray.length; i++) {
      if (seatCoords[uniqueSeatIdArray[i]]) {
        coordsArray.push(seatCoords[uniqueSeatIdArray[i]]);
      }
    }
  }

  return coordsArray;
};

const getCenterOfListing = (seatCoords, uniqueSeatIds) => {
  let center = null;
  if (Array.isArray(uniqueSeatIds) && uniqueSeatIds.length > 0 && seatCoords) {
    const coordsArray = getsCoordsOfAGroupOfSeats(seatCoords, uniqueSeatIds);
    if (coordsArray.length > 0) {
      center = getMiddleOfCoordsArray(coordsArray);
    }
  }

  return center;
};

const getMiddleOfCoordsArray = (coordsArray) => {
  let middle = null;
  if (coordsArray.length === 1) {
    [middle] = coordsArray;
  }
  if (coordsArray.length > 1) {
    if (coordsArray.length % 2 !== 0) {
      middle = coordsArray[Math.floor(coordsArray.length / 2)];
    } else {
      const left = coordsArray[Math.floor(coordsArray.length / 2) - 1];
      const right = coordsArray[Math.floor(coordsArray.length / 2)];
      middle = [(left[0] + right[0]) / 2, (left[1] + right[1]) / 2];
    }
  }

  return middle;
};

const tapArea = (point, width, height) => {
  return [
    [point.x - width, point.y - height],
    [point.x + width, point.y + height],
  ];
};

const addMapSource = (props) => {
  return [
    {
      id: LAYER_BACKGROUND_SOURCE,
      type: 'image',
      url: `${IMAGE_SOURCE_URL}/${venueConfigId(props)}/field.png`,
      coordinates: [
        /* eslint-disable no-magic-numbers */
        [0, 0.4096],
        [0.4096, 0.4096],
        [0.4096, 0],
        [0, 0],
        /* eslint-enable no-magic-numbers */
      ],
    },
    {
      id: LAYER_SECTIONS_SOURCE,
      type: 'vector',
      url: urlWithMode(getSectionsTilesetURL(venueConfigId(props)), props.mapboxMode),
    },
  ];
};

const getAvailableSectionsForZones = (zoneList, zonesSectionsMap) => {
  if (zoneList) {
    if (Array.isArray(zoneList)) {
      return zoneList.reduce((sectionIds, zone) => [...sectionIds, ...(zonesSectionsMap[zone] || [])], []);
    }

    return zonesSectionsMap[zoneList] || [];
  }

  return [];
};

export {
  getSectionsTilesetURL,
  getSectionsSourceLayer,
  getRowsSourceLayer,
  getSeatsSourceLayer,
  getFeatureCoordinates,
  getListingIdFromListing,
  getSectionRowSeatFromListing,
  safeAddLayer,
  safeRemoveLayer,
  safeAddSource,
  updateLayer,
  layerExists,
  mapIdsToValueStyle,
  getUniqueSeatIdsFromListing,
  getUniqueRowIdsFromListing,
  seatProps,
  rowProps,
  getListingFromSectionRowSeatIds,
  getListingsFromSectionRowIds,
  getLowestPriceOfARow,
  splitVectorMinimumQuantity,
  getValidOrMinimumQuantityFromSplitVector,
  venueConfigId,
  flattenMultipolygonCoords,
  getRowFeatureByUniqueRowId,
  getSeatFeatureByUniqueSeatId,
  getsCoordsOfAGroupOfSeats,
  getMiddleOfCoordsArray,
  getCenterOfListing,
  tapArea,
  urlWithMode,
  addMapSource,
  getAvailableSectionsForZones,
};
